// Client profile — Calyvra Client ID, workspace relationship history/timeline,
// linked cases, and (for Calyvra Admins) transfer between workspaces.
import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { ArrowLeft, IdCard, Loader2, ArrowRightLeft, Building2, FileText, Eye, Plus, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ClientWorkbench } from '../components/ClientWorkbench';
import { EditClientDialog } from '../components/EditClientDialog';
import { toast } from 'sonner';

export function ClientDetailPage() {
  const { id } = useParams();
  const { hasPermission, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [targetWs, setTargetWs] = useState('');
  const [athena, setAthena] = useState('');
  const [reason, setReason] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [ssnRevealed, setSsnRevealed] = useState(null);
  const [creatingCase, setCreatingCase] = useState(false);
  const [agents, setAgents] = useState([]);
  const [constants, setConstants] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [addGroupWs, setAddGroupWs] = useState('');
  const [addGroupAthena, setAddGroupAthena] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);

  const canManage = hasPermission('clients.manage');
  const canRevealSsn = hasPermission('cases.reveal_ssn');
  const canCreateCase = hasPermission('cases.create');

  const updatePrimaryAgent = async (agentId) => {
    try {
      await portalApi.updateClient(id, { primary_agent_id: agentId || '' });
      toast.success('Primary Agent updated');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const updateAuthorizedAgents = async (ids) => {
    try {
      await portalApi.updateClient(id, { authorized_agent_ids: ids });
      toast.success('Agent access updated');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const [actionMsg, setActionMsg] = useState('');
  const [actionRequired, setActionRequired] = useState(false);
  const [savingAction, setSavingAction] = useState(false);

  const saveAgentAction = async () => {
    if (!actionMsg.trim()) { toast.error('Write a note first'); return; }
    setSavingAction(true);
    try {
      await portalApi.setAgentAction(id, actionMsg.trim(), actionRequired);
      toast.success('Note sent to the assigned agent');
      setActionMsg(''); setActionRequired(false);
      load();
    } catch (err) { toast.error(err.message); } finally { setSavingAction(false); }
  };

  const resolveAction = async () => {
    try {
      await portalApi.resolveAgentAction(id);
      toast.success('Marked as done');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const revealSsn = async () => {
    try {
      const r = await portalApi.revealClientSsn(id);
      setSsnRevealed(r);
    } catch (err) { toast.error(err.message); }
  };

  const createCase = async () => {
    setCreatingCase(true);
    try {
      const r = await portalApi.createClientCase(id, { intake_type: 'New Enrollment' });
      toast.success('Case created');
      window.location.href = `/portal/cases/${r.id}`;
    } catch (err) { toast.error(err.message); } finally { setCreatingCase(false); }
  };

  const canTransfer = hasPermission('clients.transfer');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await portalApi.getClient(id));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canManage || canTransfer) portalApi.getAllWorkspaces().then(setAllWorkspaces).catch(() => {}); }, [canManage, canTransfer]);
  useEffect(() => { portalApi.getAgents().then(setAgents).catch(() => {}); }, []);
  useEffect(() => { portalApi.getConstants().then(setConstants).catch(() => {}); }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;
  if (!data) return null;

  const { client, cases } = data;
  const isStandard = data.active_client_profile === 'full';
  const activeWorkspaces = data.active_workspaces || [];
  const hasFull = activeWorkspaces.some((w) => w.client_profile === 'full');
  const hasSimplified = activeWorkspaces.some((w) => w.client_profile === 'simplified');
  const fullWs = activeWorkspaces.find((w) => w.client_profile === 'full');
  const simplifiedWs = activeWorkspaces.find((w) => w.client_profile === 'simplified');
  const activeWsIds = new Set(activeWorkspaces.map((w) => w.workspace_id));
  const activeRel = client.workspaces?.find((r) => r.status === 'Active');
  const targetWsObj = allWorkspaces.find((w) => w.id === targetWs);
  const targetIsSimplified = targetWsObj?.client_profile === 'simplified';
  const addGroupWsObj = allWorkspaces.find((w) => w.id === addGroupWs);
  const addGroupIsSimplified = addGroupWsObj?.client_profile === 'simplified';

  const addGroup = async () => {
    if (!addGroupWs) return;
    setAddingGroup(true);
    try {
      await portalApi.addClientWorkspace(id, { workspace_id: addGroupWs, athena_id: addGroupAthena || null });
      toast.success('Client added to group');
      setAddGroupOpen(false);
      setAddGroupWs(''); setAddGroupAthena('');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddingGroup(false);
    }
  };

  const createCaseIn = async (workspaceId) => {
    setCreatingCase(true);
    try {
      const r = await portalApi.createClientCase(id, { intake_type: 'New Enrollment', workspace_id: workspaceId || undefined });
      toast.success('Case created');
      window.location.href = `/portal/cases/${r.id}`;
    } catch (err) { toast.error(err.message); } finally { setCreatingCase(false); }
  };


  const doTransfer = async () => {
    if (!targetWs) return;
    setTransferring(true);
    try {
      await portalApi.transferClient(id, { target_workspace_id: targetWs, athena_id: athena || null, reason: reason || null });
      toast.success('Client transferred');
      setTransferOpen(false);
      setTargetWs(''); setAthena(''); setReason('');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTransferring(false);
    }
  };

  const d = client.demographics || {};
  const name = [d.first_name, d.last_name].filter(Boolean).join(' ');

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/portal/clients" className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm"><ArrowLeft className="h-4 w-4" /> Back to Clients</Link>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-client-btn"><Pencil className="h-4 w-4 mr-2" /> Edit Client</Button>
          )}
          {canManage && (
            <Button variant="outline" onClick={() => setAddGroupOpen(true)} data-testid="add-group-btn"><Plus className="h-4 w-4 mr-2" /> Add to Group</Button>
          )}
          {canTransfer && (
            <Button onClick={() => setTransferOpen(true)} data-testid="transfer-client-btn"><ArrowRightLeft className="h-4 w-4 mr-2" /> Transfer Workspace</Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center gap-3 mb-1">
          <IdCard className="h-6 w-6 text-sky-500" />
          <h1 className="text-2xl font-bold text-slate-900" data-testid="client-calyvra-id">{client.calyvra_client_id}</h1>
          {activeWorkspaces.length > 0
            ? activeWorkspaces.map((w) => <Badge key={w.workspace_id} className="bg-green-100 text-green-700" data-testid={`active-group-${w.workspace_key || w.workspace_id}`}>Active in {w.workspace_name}</Badge>)
            : (activeRel && <Badge className="bg-green-100 text-green-700">Active in {activeRel.workspace_name}</Badge>)}
        </div>
        <div className="grid sm:grid-cols-4 gap-4 mt-4 text-sm">
          <div><p className="text-slate-400 text-xs">Name</p><p className="text-slate-700">{name || '—'}</p></div>
          {!isStandard && (
            <div><p className="text-slate-400 text-xs">Active Athena ID</p><p className="text-slate-700">{activeRel?.athena_id || '—'}</p></div>
          )}
          <div><p className="text-slate-400 text-xs">Source</p><p className="text-slate-700">{client.source || '—'}</p></div>
          {activeRel && !activeRel.athena_id && (
            <div data-testid="client-primary-agent">
              <p className="text-slate-400 text-xs">Primary Agent / Producer</p>
              <p className="text-slate-700">{client.primary_agent_name || '—'}</p>
            </div>
          )}
          <div><p className="text-slate-400 text-xs">Phone</p><p className="text-slate-700">{d.phone || '—'}</p></div>
        </div>
      </div>

      {isAdmin && (
        <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="agent-access-card">
          <h3 className="font-semibold text-slate-900 mb-1">Agent Access</h3>
          <p className="text-xs text-slate-500 mb-4">Only you (Calyvra Administrator) can assign this client to agents. Agents see only the clients assigned to them.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label className="text-xs text-slate-500">Primary Agent / Producer</Label>
              <Select value={client.primary_agent_id || 'none'} onValueChange={(v) => updatePrimaryAgent(v === 'none' ? '' : v)}>
                <SelectTrigger className="h-9 mt-1" data-testid="primary-agent-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Additional Authorized Agents</Label>
              <div className="flex flex-wrap gap-2 mt-2 mb-2" data-testid="authorized-agents-list">
                {(client.authorized_agent_ids || []).length === 0 && <span className="text-sm text-slate-400">None</span>}
                {(client.authorized_agent_ids || []).map((aid) => {
                  const a = agents.find((x) => x.id === aid);
                  return (
                    <Badge key={aid} variant="outline" className="gap-1 text-sky-700 border-sky-200" data-testid={`authorized-agent-${aid}`}>
                      {a?.name || aid}
                      <button className="ml-1 hover:text-red-600" data-testid={`remove-authorized-${aid}`}
                        onClick={() => updateAuthorizedAgents((client.authorized_agent_ids || []).filter((x) => x !== aid))}>×</button>
                    </Badge>
                  );
                })}
              </div>
              <Select value="add" onValueChange={(v) => { if (v && v !== 'add' && !(client.authorized_agent_ids || []).includes(v)) updateAuthorizedAgents([...(client.authorized_agent_ids || []), v]); }}>
                <SelectTrigger className="h-9" data-testid="add-authorized-agent-select"><SelectValue placeholder="Add agent…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add" disabled>Add agent…</SelectItem>
                  {agents.filter((a) => a.id !== client.primary_agent_id && !(client.authorized_agent_ids || []).includes(a.id))
                    .map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-6 border-t border-slate-100 pt-4">
            <Label className="text-xs text-slate-500">Note / required action for the assigned agent</Label>
            {client.agent_action && (
              <div className={`mt-2 mb-3 p-3 rounded-lg text-sm ${client.agent_action.resolved ? 'bg-slate-50 text-slate-500' : client.agent_action.required ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-sky-50 text-sky-800 border border-sky-200'}`} data-testid="current-agent-action">
                <p className="font-medium">{client.agent_action.required ? 'Action required' : 'Note'}{client.agent_action.resolved ? ' · done' : ''}</p>
                <p>{client.agent_action.message}</p>
              </div>
            )}
            <Textarea value={actionMsg} onChange={(e) => setActionMsg(e.target.value)} placeholder="e.g. Please follow up on missing documents" className="mt-1" data-testid="agent-action-input" />
            <div className="flex items-center justify-between mt-2">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={actionRequired} onChange={(e) => setActionRequired(e.target.checked)} data-testid="agent-action-required" />
                Mark as required action
              </label>
              <Button size="sm" onClick={saveAgentAction} disabled={savingAction} data-testid="save-agent-action-btn">
                {savingAction ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send to agent'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!isAdmin && client.agent_action && !client.agent_action.resolved && (
        <div className={`rounded-xl p-5 border ${client.agent_action.required ? 'bg-amber-50 border-amber-300' : 'bg-sky-50 border-sky-200'}`} data-testid="agent-action-banner">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className={`font-semibold ${client.agent_action.required ? 'text-amber-800' : 'text-sky-800'}`}>
                {client.agent_action.required ? 'Action required' : 'Note from your administrator'}
              </p>
              <p className="text-sm text-slate-700 mt-1">{client.agent_action.message}</p>
              <p className="text-xs text-slate-400 mt-1">From {client.agent_action.created_by_name}</p>
            </div>
            <Button size="sm" variant="outline" onClick={resolveAction} data-testid="resolve-action-btn">Mark done</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="client-enrollment">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">Demographics &amp; Enrollment</h3>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div><p className="text-slate-400 text-xs">Full Name</p><p className="text-slate-700">{[d.first_name, d.middle_name, d.last_name].filter(Boolean).join(' ') || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Date of Birth</p><p className="text-slate-700">{d.date_of_birth || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Sex</p><p className="text-slate-700">{d.sex || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Preferred Language</p><p className="text-slate-700">{d.preferred_language || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Email</p><p className="text-slate-700">{d.email || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Phone</p><p className="text-slate-700">{d.phone || '—'}</p></div>
          <div className="sm:col-span-2"><p className="text-slate-400 text-xs">Address</p><p className="text-slate-700">{[d.street_address, d.apartment, d.city, d.state, d.zip_code, d.county].filter(Boolean).join(', ') || '—'}</p></div>
          <div>
            <p className="text-slate-400 text-xs">SSN</p>
            <p className="text-slate-700 flex items-center gap-2" data-testid="client-ssn">
              {ssnRevealed?.ssn || d.ssn || '—'}
              {d.ssn && canRevealSsn && !ssnRevealed && (
                <button onClick={revealSsn} className="text-sky-600 hover:text-sky-700" data-testid="reveal-ssn-btn" title="Reveal SSN"><Eye className="h-4 w-4" /></button>
              )}
            </p>
          </div>
          <div><p className="text-slate-400 text-xs">Immigration Status</p><p className="text-slate-700">{d.immigration_status || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Marital Status</p><p className="text-slate-700">{d.marital_status || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Occupation</p><p className="text-slate-700">{d.occupation || '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Income</p><p className="text-slate-700">{d.income_amount != null ? `$${Number(d.income_amount).toLocaleString()} / ${d.income_period || 'annual'}` : '—'}</p></div>
          <div><p className="text-slate-400 text-xs">Currently Smokes</p><p className="text-slate-700">{d.currently_smokes == null ? '—' : (d.currently_smokes ? 'Yes' : 'No')}</p></div>
          <div><p className="text-slate-400 text-xs">Will File Taxes</p><p className="text-slate-700">{d.will_file_taxes == null ? '—' : (d.will_file_taxes ? 'Yes' : 'No')}</p></div>
        </div>
        {d.spouse && (d.spouse.first_name || d.spouse.last_name) && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-slate-500 font-medium text-sm mb-2">Spouse</p>
            <div className="grid sm:grid-cols-4 gap-4 text-sm">
              <div><p className="text-slate-400 text-xs">Name</p><p className="text-slate-700">{[d.spouse.first_name, d.spouse.last_name].filter(Boolean).join(' ') || '—'}</p></div>
              <div><p className="text-slate-400 text-xs">DOB</p><p className="text-slate-700">{d.spouse.date_of_birth || '—'}</p></div>
              <div><p className="text-slate-400 text-xs">Occupation</p><p className="text-slate-700">{d.spouse.occupation || '—'}</p></div>
              <div><p className="text-slate-400 text-xs">Filing Jointly</p><p className="text-slate-700">{d.spouse.file_jointly == null ? '—' : (d.spouse.file_jointly ? 'Yes' : 'No')}</p></div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Building2 className="h-5 w-5 text-sky-500" /> Workspace History</h3>
        <div className="space-y-3" data-testid="workspace-history">
          {client.workspaces?.map((r, i) => (
            <div key={i} className="flex items-center gap-3 border-l-2 border-slate-200 pl-4 py-1">
              <span className="text-slate-800 font-medium">{r.workspace_name}</span>
              <Badge className={r.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>{r.status}</Badge>
              {r.athena_id && <span className="text-xs text-slate-400">Athena {r.athena_id}</span>}
              <span className="text-xs text-slate-400">
                {(r.started_at || '').slice(0, 10)}{r.ended_at ? ` → ${r.ended_at.slice(0, 10)}` : ' → present'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Calyvra (full profile) — client-level policies incl. dental/life/etc. */}
      {(hasFull || (!hasFull && !hasSimplified && isStandard)) && (
        <div className="space-y-2">
          {hasFull && hasSimplified && (
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Building2 className="h-5 w-5 text-indigo-500" /> {fullWs?.workspace_name || 'Calyvra'} — Policies</h3>
          )}
          <ClientWorkbench clientId={client.id} constants={constants} canEdit={canManage} />
        </div>
      )}

      {/* Case-based groups (e.g. Latinos Salud health cases) */}
      {(hasSimplified || (!hasFull && !hasSimplified && !isStandard)) && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><FileText className="h-5 w-5 text-sky-500" /> {hasFull && simplifiedWs ? `${simplifiedWs.workspace_name} — Cases` : 'Cases'} ({cases.length})</h3>
            {canCreateCase && (
              <Button size="sm" onClick={() => createCaseIn(simplifiedWs?.workspace_id)} disabled={creatingCase} data-testid="create-case-btn">
                {creatingCase ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Create Case
              </Button>
            )}
          </div>
          {cases.length === 0 ? <p className="text-sm text-slate-400">No cases.</p> : (
            <div className="space-y-2">
              {cases.map((c) => (
                <Link key={c.id} to={`/portal/cases/${c.id}`} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50" data-testid={`client-case-${c.id}`}>
                  <span className="text-slate-700">{c.coverage_year_label} · Athena {c.athena_id}</span>
                  <Badge className="bg-slate-100 text-slate-600">{c.status}</Badge>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      <EditClientDialog open={editOpen} onOpenChange={setEditOpen} client={client} constants={constants} onSaved={load} />

      <Dialog open={addGroupOpen} onOpenChange={setAddGroupOpen}>
        <DialogContent data-testid="add-group-dialog">
          <DialogHeader><DialogTitle>Add {client.calyvra_client_id} to a group</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">The same person can belong to several groups at once (e.g. Latinos Salud for health and Calyvra for dental/life). No duplicate record is created.</p>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Group / Workspace</Label>
              <Select value={addGroupWs} onValueChange={setAddGroupWs}>
                <SelectTrigger data-testid="add-group-select"><SelectValue placeholder="Select a group" /></SelectTrigger>
                <SelectContent>
                  {allWorkspaces.filter((w) => !activeWsIds.has(w.id)).map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {addGroupIsSimplified && (
              <div className="space-y-2">
                <Label>Athena ID (required for {addGroupWsObj?.name})</Label>
                <Input value={addGroupAthena} onChange={(e) => setAddGroupAthena(e.target.value)} placeholder="Enter Athena ID" data-testid="add-group-athena" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddGroupOpen(false)}>Cancel</Button>
            <Button onClick={addGroup} disabled={addingGroup || !addGroupWs} data-testid="confirm-add-group-btn">{addingGroup ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add to Group'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>Transfer {client.calyvra_client_id}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">The Client ID and all historical records are preserved. The current workspace becomes Inactive.</p>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Target Workspace</Label>
              <Select value={targetWs} onValueChange={setTargetWs}>
                <SelectTrigger data-testid="transfer-target-select"><SelectValue placeholder="Select workspace" /></SelectTrigger>
                <SelectContent>
                  {allWorkspaces.filter((w) => w.id !== client.active_workspace_id).map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {targetIsSimplified && (
              <div className="space-y-2">
                <Label>Athena ID (required for Latinos Salud)</Label>
                <Input value={athena} onChange={(e) => setAthena(e.target.value)} placeholder="Enter Athena ID" data-testid="transfer-athena" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button onClick={doTransfer} disabled={transferring || !targetWs} data-testid="confirm-transfer-btn">{transferring ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Transfer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ClientDetailPage;
